import React, {Suspense, useEffect} from 'react';

import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
} from 'react-router-dom';

import {HelmetProvider} from 'react-helmet-async';

import {ThemeProvider} from './Utils/ThemeContext';
import Header from './Elements/Header';
import Footer from './Elements/Footer';
import CookieBanner from './Elements/CookieBanner';
import SEO from './Components/SEO';
import LoadingSpinner from './Components/LoadingSpinner';
import ErrorBoundary from './Pages/ErrorBoundary';

import {ROUTES, NOT_FOUND_SEO, NotFoundPage} from './routes';

import {SITE_CONFIG} from './site.config';

/* -------------------- Viewport Height Fix -------------------- */
function useViewportHeightFix() {
    useEffect(() => {
        const setVh = () =>
            document.documentElement.style.setProperty(
                '--vh',
                `${window.innerHeight * 0.01}px`,
            );
        setVh();
        window.addEventListener('resize', setVh);
        return () => window.removeEventListener('resize', setVh);
    }, []);
}

/* -------------------- Scroll to top on route change -------------------- */
function ScrollToTop() {
    const {pathname} = useLocation();
    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }, [pathname]);
    return null;
}

/* -------------------- Skip Navigation Link -------------------- */
const SKIP_LINK = (
    <a
        href='#main-content'
        className='sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-[rgb(var(--bg-primary))] focus:text-[rgb(var(--text-primary))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-primary))] rounded-lg'
    >
        Skip to main content
    </a>
);

/* -------------------- Maintenance Page -------------------- */
const MaintenancePage: React.FC = () => (
    <div className='min-h-screen flex items-center justify-center bg-[rgb(var(--bg-primary))] p-4'>
        <div className='text-center max-w-lg'>
            <div className='mb-6'>
                <div className='w-20 h-20 mx-auto rounded-full bg-[rgb(var(--accent-primary))]/10 flex items-center justify-center'>
                    <svg
                        className='w-10 h-10 text-[rgb(var(--accent-primary))]'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                        aria-hidden='true'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'
                        />
                    </svg>
                </div>
            </div>

            <h1 className='text-3xl font-bold mb-4 text-[rgb(var(--text-primary))]'>
                Under Maintenance
            </h1>

            <p className='text-[rgb(var(--text-secondary))] mb-6 leading-relaxed'>
                We're currently performing scheduled maintenance. We'll be back
                shortly.
            </p>

            <div className='text-sm text-[rgb(var(--text-secondary))]'>
                <p>Thank you for your patience.</p>
                <p className='mt-2'>
                    Contact:{' '}
                    <a
                        href={`mailto:${SITE_CONFIG.contact.email}`}
                        className='text-[rgb(var(--accent-primary))] hover:underline'
                    >
                        {SITE_CONFIG.contact.email}
                    </a>
                </p>
            </div>
        </div>
    </div>
);

/* -------------------- Layout -------------------- */
function AppLayout() {
    useViewportHeightFix();

    /* Check maintenance mode - simple boolean toggle */
    if (SITE_CONFIG.maintenanceMode) {
        return (
            <>
                <SEO
                    title='Maintenance'
                    description='Site under maintenance'
                    noIndex
                />
                <MaintenancePage />
            </>
        );
    }

    return (
        <div className='min-h-screen flex flex-col'>
            {SKIP_LINK}
            <Header />
            <main id='main-content' className='flex-grow'>
                <ScrollToTop />
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        {ROUTES.map(({path, seo, element: PageComponent}) => (
                            <Route
                                key={path}
                                path={path}
                                element={
                                    <>
                                        <SEO {...seo} />
                                        <PageComponent />
                                    </>
                                }
                            />
                        ))}
                        <Route
                            path='*'
                            element={
                                <>
                                    <SEO {...NOT_FOUND_SEO} />
                                    <NotFoundPage />
                                </>
                            }
                        />
                    </Routes>
                </Suspense>
            </main>
            <Footer />
            <CookieBanner />
        </div>
    );
}

/* -------------------- Root -------------------- */
function App() {
    return (
        <HelmetProvider>
            <Router>
                <ThemeProvider>
                    <ErrorBoundary>
                        <AppLayout />
                    </ErrorBoundary>
                </ThemeProvider>
            </Router>
        </HelmetProvider>
    );
}

export default App;
